import React, { FC, ReactNode, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { Modal } from "@mapmycustomers/ui";

import UsersSelect from "@app/component/UsersSelect";
import { isCurrentUserOwner } from "@app/store/iam";
import { isOwner } from "@app/store/iam/util";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import styles from "./AssignModal.module.scss";

const messages = defineMessages({
  title: {
    id: "onboarding.guides.assignModal",
    defaultMessage: "Assign Users",
    description: "Title of the Assign Users modal on the Guides page",
  },
});

export interface AssignModalProps {
  allowEmpty?: boolean;
  onChange: (userIds: User["id"][]) => void;
  onHide: () => void;
  subTitle?: ReactNode;
  value?: User["id"][];
}

interface Props extends AssignModalProps {
  isCurrentUserOwner: boolean;
  teams: Team[];
  users: User[];
}

const AssignModal: FC<Props> = ({
  allowEmpty,
  isCurrentUserOwner,
  onChange,
  onHide,
  subTitle,
  teams,
  users,
  value,
}) => {
  const intl = useIntl();

  const filteredUsers = useMemo(
    () => users.filter((user) => (isOwner(user) ? isCurrentUserOwner : true)),
    [isCurrentUserOwner, users]
  );

  const handleApply = useCallback(
    (userIds: User["id"][]) => {
      onChange(userIds);
      onHide();
    },
    [onChange, onHide]
  );

  return (
    <Modal
      className={styles.container}
      footer={null}
      onCancel={onHide}
      open
      title={intl.formatMessage(messages.title)}
      width="auto"
    >
      {subTitle && <p className={styles.subTitle}>{subTitle}</p>}
      <UsersSelect
        allowEmpty={allowEmpty}
        allUsersWhenEmpty={false}
        onApply={handleApply}
        onCancel={onHide}
        selectedUserIds={value}
        teams={teams}
        users={filteredUsers}
      />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  isCurrentUserOwner: isCurrentUserOwner(state),
  teams: getTeams(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(AssignModal);
